// Chemin: C:\gestion-copro\src\components\messagerie\ComposeEmailModal.tsx
// Modale de composition email - Copro Protect
// Session 86 : Refonte complète destinataires (identique à CourrierFormPage)
//   - Ajout copropriétaires avec badge ★ CS
//   - Radio buttons : Carnet d'adresses / Saisie manuelle
//   - Props prefill pour pré-remplissage depuis SENTINELLE

import React, { useState, useEffect } from 'react';
import { X, Send, Shield, Loader2, AlertTriangle, Users } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { sendEmail } from '../../services/gmail.service';
import { analyserCourrierSCUD } from '../../services/courrier.service';
import { getInterlocuteurs } from '../../services/interlocuteur.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
import { getCoproprietaires } from '../../services/coproprietaire.service';
import { createMessageCS, soumettreAuVoteCS } from '../../services/messageCS.service';
import { Interlocuteur } from '../../types/interlocuteur.types';
import { Coproprietaire } from '../../types/coproprietaire.types';
import { MembreCS } from '../../types/conseilSyndical.types';
import { AnalyseSCUD, NatureCommunication, NiveauRisque } from '../../types/courrier.types';
import { CreateMessageCSData, AnalyseSentinelleMessage } from '../../types/messageCS.types';
import { useAuth } from '../../context/AuthContext';

// Composants SENTINELLE
import ProtectionSentinelleModal from '../sentinelle/ProtectionSentinelleModal';
import AvertissementSentinelleModal from '../sentinelle/AvertissementSentinelleModal';
import TransformerCourrierModal from '../sentinelle/TransformerCourrierModal';

// ============================================================================
// TYPES
// ============================================================================

export interface EmailPrefillData {
  destinataire?: string;
  objet?: string;
  contenu?: string;
  affaireId?: string;
}

interface ComposeEmailModalProps {
  coproprieteId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onRequireValidation?: (emailData: { 
    to: string; 
    subject: string; 
    body: string; 
    destinataireNom: string;
    nature: NatureCommunication;
    risque: NiveauRisque;
  }) => void;
  onTransformToCourrier?: (emailData: {
    to: string;
    subject: string;
    body: string;
    destinataireNom: string;
  }) => void;
  prefill?: EmailPrefillData;
}

type ModalStep = 'compose' | 'analyze' | 'sending' | 'submitting_vote';
type DestinataireType = 'carnet' | 'manuel';

interface InterlocuteurAvecEmail {
  id: string;
  raisonSociale: string;
  type: string;
  email: string;
  contactNom: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const getContactPrincipalEmail = (interlocuteur: Interlocuteur): { email: string; nom: string } | null => {
  const principal = interlocuteur.contacts.find(c => c.principal && c.email);
  if (principal && principal.email) {
    const nomContact = principal.prenom 
      ? `${principal.prenom} ${principal.nom}` 
      : principal.nom;
    return { email: principal.email, nom: nomContact };
  }
  
  const avecEmail = interlocuteur.contacts.find(c => c.email);
  if (avecEmail && avecEmail.email) {
    const nomContact = avecEmail.prenom 
      ? `${avecEmail.prenom} ${avecEmail.nom}` 
      : avecEmail.nom;
    return { email: avecEmail.email, nom: nomContact };
  }
  
  return null;
};

// Convertir AnalyseSCUD vers AnalyseSentinelleMessage
const convertToAnalyseSentinelle = (scud: AnalyseSCUD): AnalyseSentinelleMessage => {
  return {
    risque: scud.scoreRisque,
    nature: scud.nature || 'courante',
    formulationsDetectees: scud.alertes?.map(a => ({
      original: a.extrait || '',
      categorie: 'autre' as const,
      explication: a.message,
      suggestion: a.suggestion,
    })),
    dateAnalyse: scud.dateAnalyse,
  };
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
  coproprieteId,
  onClose,
  onSuccess,
  onTransformToCourrier,
  prefill,
}) => {
  const { firebaseUser, userProfile } = useAuth();
  
  // États du formulaire
  const [destinataireType, setDestinataireType] = useState<DestinataireType>('carnet');
  const [selectedValue, setSelectedValue] = useState('');
  const [to, setTo] = useState('');
  const [toNom, setToNom] = useState('');
  const [toInterlocuteurId, setToInterlocuteurId] = useState<string | undefined>();
  const [subject, setSubject] = useState(prefill?.objet || '');
  const [body, setBody] = useState(prefill?.contenu || '');
  const [error, setError] = useState<string | null>(null);
  
  // États du workflow
  const [step, setStep] = useState<ModalStep>('compose');
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Données chargées
  const [interlocuteurs, setInterlocuteurs] = useState<InterlocuteurAvecEmail[]>([]);
  const [coproprietaires, setCoproprietaires] = useState<Coproprietaire[]>([]);
  const [membresCSNoms, setMembresCSNoms] = useState<Set<string>>(new Set());
  const [loadingData, setLoadingData] = useState(true);
  
  // Nombre de membres CS pour le vote
  const [nombreMembresCS, setNombreMembresCS] = useState<number>(3);
  
  // Analyse SCUD
  const [analyseSCUD, setAnalyseSCUD] = useState<AnalyseSCUD | null>(null);
  
  // Modals SENTINELLE
  const [showProtectionModal, setShowProtectionModal] = useState(false);
  const [showAvertissementModal, setShowAvertissementModal] = useState(false);
  const [showTransformerModal, setShowTransformerModal] = useState(false);
  
  // Reformulation
  const [isLoadingReformulation, setIsLoadingReformulation] = useState(false);

  // Charger les données au montage
  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Charger les interlocuteurs avec email
        const interlocuteursData = await getInterlocuteurs(coproprieteId);
        const interlocuteursAvecEmail: InterlocuteurAvecEmail[] = [];
        
        interlocuteursData.forEach((interlocuteur: Interlocuteur) => {
          if (interlocuteur.actif) {
            const contact = getContactPrincipalEmail(interlocuteur);
            if (contact) {
              interlocuteursAvecEmail.push({
                id: interlocuteur.id,
                raisonSociale: interlocuteur.raisonSociale,
                type: interlocuteur.type,
                email: contact.email,
                contactNom: contact.nom,
              });
            }
          }
        });
        setInterlocuteurs(interlocuteursAvecEmail);
        
        // 2. Charger les copropriétaires
        const coproprietairesData = await getCoproprietaires(coproprieteId);
        setCoproprietaires(coproprietairesData);
        
        // 3. Charger les membres CS pour le badge ★
        const membresCS = await getMembresCSByCopropriete(coproprieteId, true);
        const csNoms = new Set<string>();
        membresCS.forEach((membre: MembreCS) => {
          const nomComplet = `${membre.prenom} ${membre.nom}`.trim().toLowerCase();
          csNoms.add(nomComplet);
        });
        setMembresCSNoms(csNoms);
        
        // 4. Calculer votesRequis (exclure le rédacteur)
        const membresActifs = membresCS.filter((m: MembreCS) => m.actif !== false);
        const membresVotants = membresActifs.filter((m: MembreCS) => 
          m.userId !== firebaseUser?.uid
        );
        setNombreMembresCS(membresVotants.length > 0 ? membresVotants.length : 1);
        
        // 5. Si prefill avec destinataire, essayer de le trouver
        if (prefill?.destinataire) {
          const searchTerm = prefill.destinataire.toLowerCase();
          
          const foundInterlocuteur = interlocuteursAvecEmail.find(i => 
            i.raisonSociale.toLowerCase().includes(searchTerm) ||
            i.type.toLowerCase() === searchTerm ||
            i.email.toLowerCase() === searchTerm
          );
          
          if (foundInterlocuteur) {
            setSelectedValue(`interlocuteur:${foundInterlocuteur.id}`);
            setTo(foundInterlocuteur.email);
            setToNom(`${foundInterlocuteur.contactNom} (${foundInterlocuteur.raisonSociale})`);
            setToInterlocuteurId(foundInterlocuteur.id);
          } else {
            const foundCopro = coproprietairesData.find((c: Coproprietaire) => {
              const nomComplet = `${c.prenom || ''} ${c.nom}`.trim().toLowerCase();
              return nomComplet.includes(searchTerm) || 
                     (c.email && c.email.toLowerCase() === searchTerm);
            });
            
            if (foundCopro && foundCopro.email) {
              setSelectedValue(`coproprietaire:${foundCopro.id}`);
              setTo(foundCopro.email);
              setToNom(`${foundCopro.prenom || ''} ${foundCopro.nom}`.trim());
            } else if (prefill.destinataire.includes('@')) {
              setDestinataireType('manuel');
              setTo(prefill.destinataire);
            }
          }
        }
        
      } catch (err) {
        console.error('Erreur chargement données:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [coproprieteId, prefill, firebaseUser?.uid]);

  // Handler sélection destinataire
  const handleDestinataireSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedValue(value);
    
    if (!value) {
      setTo('');
      setToNom('');
      setToInterlocuteurId(undefined);
      return;
    }
    
    const [type, id] = value.split(':');
    
    if (type === 'interlocuteur') {
      const interlocuteur = interlocuteurs.find(i => i.id === id);
      if (interlocuteur) {
        setTo(interlocuteur.email);
        setToNom(`${interlocuteur.contactNom} (${interlocuteur.raisonSociale})`);
        setToInterlocuteurId(interlocuteur.id);
      }
    } else if (type === 'coproprietaire') {
      const copro = coproprietaires.find(c => c.id === id);
      if (copro && copro.email) {
        setTo(copro.email);
        setToNom(`${copro.prenom || ''} ${copro.nom}`.trim());
        setToInterlocuteurId(undefined);
      }
    }
  };

  // Handler changement type destinataire
  const handleDestinataireTypeChange = (type: DestinataireType) => {
    setDestinataireType(type);
    if (type === 'manuel') {
      setSelectedValue('');
      setToInterlocuteurId(undefined);
    } else {
      setTo('');
      setToNom('');
    }
  };

  // Handler analyse SENTINELLE
  const handleAnalyze = async () => {
    if (!to || !subject || !body) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    setError(null);
    setStep('analyze');
    setIsAnalyzing(true);

    try {
      // analyserCourrierSCUD(coproprieteId, objet, contenu, destinataire, typeCourrier)
      const result = await analyserCourrierSCUD(
        coproprieteId,
        subject,
        body,
        toNom || to,
        'information'
      );

      setAnalyseSCUD(result);

      if (result.scoreRisque === 'eleve') {
        setShowProtectionModal(true);
      } else if (result.scoreRisque === 'moyen') {
        setShowAvertissementModal(true);
      }
    } catch (err: any) {
      console.error('Erreur analyse SCUD:', err);
      setError(err.message || 'Erreur lors de l\'analyse');
      setStep('compose');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Envoi direct
  const handleSendDirect = async () => {
    setStep('sending');
    setError(null);

    try {
      await sendEmail(coproprieteId, {
        to,
        subject,
        body,
        userId: firebaseUser?.uid || '',
        userNom: userProfile?.prenom && userProfile?.nom
          ? `${userProfile.prenom} ${userProfile.nom}`
          : userProfile?.email || 'Utilisateur',
      });
      onSuccess('Email envoyé avec succès');
    } catch (err: any) {
      console.error('Erreur envoi email:', err);
      setError(err.message || 'Erreur lors de l\'envoi');
      setStep('analyze');
    }
  };

  // Soumettre au vote CS
  const handleSoumettreVoteCS = async () => {
    setStep('submitting_vote');
    setError(null);

    try {
      const redacteurNom = userProfile?.prenom && userProfile?.nom
        ? `${userProfile.prenom} ${userProfile.nom}`
        : userProfile?.email || 'Membre CS';

      // Déterminer le rôle du rédacteur
      const redacteurRole = userProfile?.role === 'gestionnaire' 
        ? 'administrateur' 
        : 'membre_cs';

      const messageData: CreateMessageCSData = {
        coproprieteId,
        destinataireEmail: to,
        destinataireNom: toNom || to,
        interlocuteurId: toInterlocuteurId,
        objet: subject,
        contenu: body,
        redacteurId: firebaseUser?.uid || '',
        redacteurNom,
        redacteurRole: redacteurRole as 'administrateur' | 'secretaire' | 'membre_cs',
        analyseSentinelle: analyseSCUD 
          ? convertToAnalyseSentinelle(analyseSCUD)
          : { risque: 'faible', nature: 'courante' },
        statut: 'brouillon',
      };

      const messageId = await createMessageCS(messageData);
      await soumettreAuVoteCS(coproprieteId, messageId, nombreMembresCS);

      onSuccess(`Message soumis au vote du Conseil Syndical (${nombreMembresCS} vote${nombreMembresCS > 1 ? 's' : ''} requis)`);
    } catch (err: any) {
      console.error('Erreur soumission vote:', err);
      setError(err.message || 'Erreur lors de la soumission');
      setStep('analyze');
    }
  };

  // Retour composition
  const handleBackToCompose = () => {
    setStep('compose');
    setAnalyseSCUD(null);
    setShowProtectionModal(false);
    setShowAvertissementModal(false);
  };

  // Appliquer reformulation
  const handleAppliquerReformulation = (nouveauContenu: string) => {
    setBody(nouveauContenu);
    setShowProtectionModal(false);
    setStep('compose');
    setAnalyseSCUD(null);
  };

  // Modifier manuellement
  const handleModifierManuellement = () => {
    setShowProtectionModal(false);
    setShowAvertissementModal(false);
    setStep('compose');
  };

  // Demander reformulation - retourne ReformulationProposee | null
  const handleDemanderReformulation = async () => {
    setIsLoadingReformulation(true);
    try {
      const functions = getFunctions();
      const reformulerFn = httpsCallable(functions, 'reformulerMessage');
      const result = await reformulerFn({
        coproprieteId,
        objet: subject,
        contenu: body,
        alertes: analyseSCUD?.alertes || [],
      });

      const data = result.data as { reformulation?: { objet: string; contenu: string } };
      if (data.reformulation) {
        const reformulationProposee = {
          objet: data.reformulation.objet,
          contenu: data.reformulation.contenu,
        };
        setAnalyseSCUD(prev => prev ? {
          ...prev,
          reformulationProposee,
        } : null);
        return reformulationProposee;
      }
      return null;
    } catch (err) {
      console.error('Erreur reformulation:', err);
      return null;
    } finally {
      setIsLoadingReformulation(false);
    }
  };

  // Transformer en courrier
  const handleTransformerCourrier = () => {
    setShowTransformerModal(false);
    if (onTransformToCourrier) {
      onTransformToCourrier({
        to,
        subject,
        body,
        destinataireNom: toNom || to,
      });
    }
    onClose();
  };

  // Rester sur email
  const handleResterEmail = () => {
    setShowTransformerModal(false);
  };

  // Badge risque
  const renderRiskBadge = () => {
    if (!analyseSCUD) return null;
    const nature = analyseSCUD.nature || 'courante';
    
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
          analyseSCUD.scoreRisque === 'faible' ? 'bg-green-100 text-green-800' :
          analyseSCUD.scoreRisque === 'moyen' ? 'bg-orange-100 text-orange-800' :
          'bg-red-100 text-red-800'
        }`}>
          <Shield className="w-4 h-4" />
          Risque {analyseSCUD.scoreRisque}
        </span>
        
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
          nature === 'courante' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
        }`}>
          {nature === 'courante' ? 'Communication courante' : 'Communication engageante'}
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">

          {/* ETAPE 1 : COMPOSITION */}
          {step === 'compose' && (
            <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Send className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Nouveau message</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto space-y-4">
                
                {/* Destinataire */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destinataire
                  </label>
                  
                  <div className="flex flex-wrap gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="destinataireType"
                        checked={destinataireType === 'carnet'}
                        onChange={() => handleDestinataireTypeChange('carnet')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Sélectionner dans le carnet d'adresses</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="destinataireType"
                        checked={destinataireType === 'manuel'}
                        onChange={() => handleDestinataireTypeChange('manuel')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Saisie manuelle</span>
                    </label>
                  </div>
                  
                  {loadingData ? (
                    <div className="flex items-center gap-2 text-gray-500 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Chargement des contacts...
                    </div>
                  ) : destinataireType === 'carnet' ? (
                    <>
                      <select
                        value={selectedValue}
                        onChange={handleDestinataireSelect}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        
                        {interlocuteurs.length > 0 && (
                          <optgroup label="📁 INTERLOCUTEURS EXTERNES">
                            {interlocuteurs.map((interlocuteur) => (
                              <option key={`interlocuteur:${interlocuteur.id}`} value={`interlocuteur:${interlocuteur.id}`}>
                                {interlocuteur.raisonSociale} ({interlocuteur.type})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        
                        {coproprietaires.filter(c => c.email).length > 0 && (
                          <optgroup label="👥 COPROPRIÉTAIRES">
                            {[...coproprietaires]
                              .filter(c => c.email)
                              .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
                              .map((copro) => {
                                const nomComplet = `${copro.prenom || ''} ${copro.nom}`.trim();
                                const isCS = membresCSNoms.has(nomComplet.toLowerCase());
                                const displayName = `${copro.nom} ${copro.prenom || ''}`.trim();
                                return (
                                  <option key={`coproprietaire:${copro.id}`} value={`coproprietaire:${copro.id}`}>
                                    {displayName}{isCS ? ' ★ CS' : ''}
                                  </option>
                                );
                              })}
                          </optgroup>
                        )}
                      </select>
                      
                      {to && (
                        <p className="mt-2 text-sm text-gray-500">
                          📧 {to}
                        </p>
                      )}
                    </>
                  ) : (
                    <input
                      type="email"
                      value={to}
                      onChange={(e) => {
                        setTo(e.target.value);
                        setToNom('');
                      }}
                      placeholder="email@exemple.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                </div>

                {/* Objet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Objet
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Objet du message..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={10}
                    placeholder="Rédigez votre message..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  SENTINELLE analysera votre message
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={!to || !subject || !body || isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Analyser avant envoi
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ETAPE 2 : ANALYSE */}
          {step === 'analyze' && (
            <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isAnalyzing ? 'bg-blue-100' : 'bg-green-100'}`}>
                    {isAnalyzing ? (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : (
                      <Shield className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isAnalyzing ? 'Analyse en cours...' : 'Analyse SENTINELLE'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto space-y-4">
                {isAnalyzing ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                    <p className="text-gray-600">SENTINELLE analyse votre message...</p>
                  </div>
                ) : analyseSCUD ? (
                  <>
                    <div className={`p-4 rounded-lg ${
                      analyseSCUD.scoreRisque === 'faible' ? 'bg-green-50 border border-green-200' :
                      analyseSCUD.scoreRisque === 'moyen' ? 'bg-orange-50 border border-orange-200' :
                      'bg-red-50 border border-red-200'
                    }`}>
                      {renderRiskBadge()}
                      
                      {(analyseSCUD.nature || 'courante') === 'engageante' && analyseSCUD.scoreRisque === 'faible' && (
                        <p className="mt-3 text-purple-700 text-sm">
                          Cette communication engage le Conseil Syndical. Le vote des membres est requis avant envoi.
                        </p>
                      )}
                    </div>

                    {analyseSCUD.alertes && analyseSCUD.alertes.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="font-medium text-gray-900">
                          Points d'attention ({analyseSCUD.alertes.length})
                        </h3>
                        {analyseSCUD.alertes.map((alerte, index) => (
                          <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-amber-800 font-medium">{alerte.message}</p>
                            {alerte.extrait && (
                              <p className="mt-1 text-sm text-amber-700 italic">"{alerte.extrait}"</p>
                            )}
                            {alerte.suggestion && (
                              <p className="mt-2 text-sm text-amber-700">
                                <span className="font-medium">Suggestion :</span> {alerte.suggestion}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {analyseSCUD.scoreRisque === 'faible' && (analyseSCUD.nature || 'courante') === 'courante' && (
                      <p className="text-green-700">
                        Message conforme. Vous pouvez l'envoyer en confiance.
                      </p>
                    )}
                  </>
                ) : error ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                ) : null}
              </div>

              {!isAnalyzing && analyseSCUD && (
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                  <button
                    type="button"
                    onClick={handleBackToCompose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                  >
                    Modifier le message
                  </button>
                  <div className="flex gap-3">
                    {(analyseSCUD.nature || 'courante') === 'engageante' ? (
                      <button
                        type="button"
                        onClick={handleSoumettreVoteCS}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                      >
                        <Users className="w-4 h-4" />
                        Soumettre au vote du CS
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendDirect}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Envoyer
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ETAPE 3 : ENVOI */}
          {step === 'sending' && (
            <div className="p-8 flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-700">Envoi en cours...</p>
            </div>
          )}

          {/* ETAPE 4 : VOTE */}
          {step === 'submitting_vote' && (
            <div className="p-8 flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
              <p className="text-gray-700">Soumission au vote du Conseil Syndical...</p>
            </div>
          )}

        </div>
      </div>

      {/* MODALS SENTINELLE */}
      <ProtectionSentinelleModal
        isOpen={showProtectionModal}
        onClose={() => {
          setShowProtectionModal(false);
          setStep('compose');
        }}
        objetOriginal={subject}
        contenuOriginal={body}
        alertes={analyseSCUD?.alertes || []}
        reformulationProposee={analyseSCUD?.reformulationProposee}
        onAppliquerReformulation={handleAppliquerReformulation}
        onModifierManuellement={handleModifierManuellement}
        onDemanderReformulation={handleDemanderReformulation}
        isLoadingReformulation={isLoadingReformulation}
      />

      <AvertissementSentinelleModal
        isOpen={showAvertissementModal}
        onClose={() => {
          setShowAvertissementModal(false);
          setStep('compose');
        }}
        alertes={analyseSCUD?.alertes || []}
        nature={analyseSCUD?.nature || 'courante'}
        onEnvoyerQuandMeme={(analyseSCUD?.nature || 'courante') === 'courante' ? handleSendDirect : undefined}
        onSoumettreVoteCS={(analyseSCUD?.nature || 'courante') === 'engageante' ? handleSoumettreVoteCS : undefined}
        onModifier={handleModifierManuellement}
      />

      <TransformerCourrierModal
        isOpen={showTransformerModal}
        onClose={() => {
          setShowTransformerModal(false);
          setStep('compose');
        }}
        motsClesDetectes={analyseSCUD?.motsClesEngageants || []}
        onTransformer={handleTransformerCourrier}
        onResterEmail={handleResterEmail}
      />
    </>
  );
};

export default ComposeEmailModal;